const AUTH_TOKEN_KEY = "authToken";
const AUTH_COOKIE_NAME = "ammachekku_auth_token";
const AUTH_EVENT_NAME = "ammachekku-auth-changed";
const AUTH_COOKIE_LIFETIME = 60 * 60 * 24 * 180;
const WORDPRESS_LOGIN_URL = "https://ammachekku.com/log-in/";
const WORDPRESS_LOGOUT_URL = "https://ammachekku.com/";

const isBrowser = typeof window !== "undefined" && typeof document !== "undefined";

const canUseLocalTokenFallback = () => {
  if (!isBrowser) {
    return false;
  }

  const host = window.location.hostname.toLowerCase();
  return host === "localhost" || host === "127.0.0.1";
};

const getCookieDomain = () => {
  if (!isBrowser) {
    return "";
  }

  const host = window.location.hostname.toLowerCase().replace(/^www\./, "");

  if (!host || host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return "";
  }

  const parts = host.split(".");

  if (parts.length < 2) {
    return host;
  }

  return `.${parts.slice(-2).join(".")}`;
};

const dispatchAuthChanged = (token) => {
  if (!isBrowser) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(AUTH_EVENT_NAME, {
      detail: { token: token || "" },
    })
  );
};

const readCookie = (name) => {
  if (!isBrowser) {
    return "";
  }

  const cookiePrefix = `${name}=`;
  const matchedCookie = document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(cookiePrefix));

  if (!matchedCookie) {
    return "";
  }

  return decodeURIComponent(matchedCookie.slice(cookiePrefix.length));
};

const writeCookie = (token) => {
  if (!isBrowser) {
    return;
  }

  const domain = getCookieDomain();
  const baseSegments = [`${AUTH_COOKIE_NAME}=${encodeURIComponent(token || "")}`, "path=/", "SameSite=Lax"];

  if (domain) {
    baseSegments.push(`domain=${domain}`);
  }

  if (window.location.protocol === "https:") {
    baseSegments.push("Secure");
  }

  if (token) {
    baseSegments.push(`max-age=${AUTH_COOKIE_LIFETIME}`);
  } else {
    baseSegments.push("expires=Thu, 01 Jan 1970 00:00:00 GMT", "max-age=0");
  }

  document.cookie = baseSegments.join("; ");

  if (!token) {
    const segmentsWithoutDomain = baseSegments.filter((segment) => !segment.startsWith("domain="));
    document.cookie = segmentsWithoutDomain.join("; ");
  }
};

export const getStoredAuthToken = () => {
  if (!isBrowser) {
    return "";
  }

  return localStorage.getItem(AUTH_TOKEN_KEY) || "";
};

export const getSharedAuthToken = () => readCookie(AUTH_COOKIE_NAME);

export const writeAuthToken = (token) => {
  if (!isBrowser) {
    return;
  }

  if (!token) {
    clearAuthToken();
    return;
  }

  localStorage.setItem(AUTH_TOKEN_KEY, token);
  writeCookie(token);
  dispatchAuthChanged(token);
};

export const clearAuthToken = () => {
  if (!isBrowser) {
    return;
  }

  localStorage.removeItem(AUTH_TOKEN_KEY);
  writeCookie("");
  dispatchAuthChanged("");
};

export const syncAuthTokenFromCookie = () => {
  if (!isBrowser) {
    return "";
  }

  const cookieToken = getSharedAuthToken();
  const localToken = getStoredAuthToken();

  // Shared cookie is source of truth across ammachekku.com + shop.ammachekku.com
  if (cookieToken) {
    if (cookieToken !== localToken) {
      localStorage.setItem(AUTH_TOKEN_KEY, cookieToken);
      dispatchAuthChanged(cookieToken);
    }
    return cookieToken;
  }

  // Cookie cleared (logout on the other subdomain) — drop local copy so it
  // cannot re-seed the shared cookie and revive the session.
  if (localToken) {
    if (canUseLocalTokenFallback()) {
      return localToken;
    }

    localStorage.removeItem(AUTH_TOKEN_KEY);
    dispatchAuthChanged("");
  }

  return "";
};

export const getCurrentAuthToken = () =>
  syncAuthTokenFromCookie() || (canUseLocalTokenFallback() ? getStoredAuthToken() : "");

/**
 * Best-effort Sanctum token revoke before clearing the shared cookie.
 * Failures are ignored so logout still clears the client session.
 */
export const revokeAuthToken = async (apiBaseUrl) => {
  if (!isBrowser) {
    return;
  }

  const token = getCurrentAuthToken();
  if (!token || !apiBaseUrl) {
    return;
  }

  const root = String(apiBaseUrl).replace(/\/+$/, "");

  try {
    await fetch(`${root}/logout`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    // ignore network errors — local logout must still proceed
  }
};

export const getWordpressLoginUrl = (redirectTo) => {
  const targetUrl = redirectTo || (isBrowser ? window.location.href : "");

  if (!targetUrl) {
    return WORDPRESS_LOGIN_URL;
  }

  try {
    const loginUrl = new URL(WORDPRESS_LOGIN_URL);
    loginUrl.searchParams.set("redirect_to", targetUrl);
    return loginUrl.toString();
  } catch (error) {
    return `${WORDPRESS_LOGIN_URL}?redirect_to=${encodeURIComponent(targetUrl)}`;
  }
};

export const getWordpressLogoutUrl = (redirectTo) => {
  const targetUrl = redirectTo || (isBrowser ? window.location.href : "");

  if (!targetUrl) {
    return `${WORDPRESS_LOGOUT_URL}?ammachekku_react_logout=1`;
  }

  try {
    const logoutUrl = new URL(WORDPRESS_LOGOUT_URL);
    logoutUrl.searchParams.set("ammachekku_react_logout", "1");
    logoutUrl.searchParams.set("redirect_to", targetUrl);
    return logoutUrl.toString();
  } catch (error) {
    return `${WORDPRESS_LOGOUT_URL}?ammachekku_react_logout=1&redirect_to=${encodeURIComponent(targetUrl)}`;
  }
};

export const AUTH_TOKEN_EVENT = AUTH_EVENT_NAME;